import { useCallback, useState } from "react";
import { toast } from "sonner";

/**
 * Email Send Guard (2026-02) — shared client-side plumbing for the two-step confirm dialog.
 * Usage:
 *   const guard = useEmailGuard();
 *   const run = async () => {
 *     const r = await api.post(url, body);
 *     if (guard.intercept(r.data, (typedCount) => api.post(url, { ...body, confirmation_token: r.data.confirmation_token, confirmed_count: typedCount }).then(onDone))) return;
 *     onDone(r.data);
 *   };
 * `intercept(data, onConfirm)` returns true (and opens the modal) when `data.confirmation_required`
 * is set; the caller should stop and return immediately. `onConfirm` is the SAME request, just
 * with the token attached — its resolved value is passed straight to the original caller's own
 * success handling via the Promise `onConfirm` returns.
 */
export function useEmailGuard() {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pendingConfirm, setPendingConfirm] = useState(null);

  const intercept = useCallback((data, onConfirm) => {
    if (!data?.confirmation_required) return false;
    setPreview(data.preview);
    setPendingConfirm(() => onConfirm);
    return true;
  }, []);

  const cancel = useCallback(() => {
    setPreview(null);
    setPendingConfirm(null);
  }, []);

  const confirm = useCallback(async (typedCount) => {
    if (!pendingConfirm) return;
    setBusy(true);
    try {
      await pendingConfirm(typedCount);
      setPreview(null);
      setPendingConfirm(null);
    } catch (e) {
      // Send Guard confirm must never fail silently (2026-02 fix — an expired/already-used
      // token, a 4xx/5xx from the real send, or a network error used to leave the modal open
      // with zero feedback). Surface the backend's own message/code and leave `preview` /
      // `pendingConfirm` untouched so the SAME dialog stays open for the user to retry.
      const detail = e?.response?.data?.detail;
      toast.error(detail?.message || detail?.code || "Couldn't send. Please try again.");
    } finally {
      setBusy(false);
    }
  }, [pendingConfirm]);

  return { preview, busy, intercept, cancel, confirm, open: !!preview };
}
